import { Autocomplete, TextField } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import useClassDescriptionOptions from '../../../console/agencies/quotes/PrimeX/requestQuote/supplementalFirmo/hooks/useClassDescriptionOptions';
import { useFormMetadataContext } from '../../context/FormMetaDataContext';
import { useOneShotEditQuoteContextSafe } from '../../../console/workflows/quotes/prime-x/views/edit-quote/one-shot/OneShotEditQuoteContext';
import { StyledInputLabel } from '../layout/FieldControlLabel';
import { ErrorMessageText } from './common';

type NaicsClassDescriptionProps = {
  name: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
};
export const CLASS_DESC_ERROR =
  'Please select a valid class description for this industry';

export const NaicsClassDescription = (props: NaicsClassDescriptionProps) => {
  const formContext = useFormContext();
  const formMetaData = useFormMetadataContext();
  const oneShotContext = useOneShotEditQuoteContextSafe();
  const isInOneShotContext = oneShotContext !== null;

  const { options: classDescriptionOptions, isLoading: isOptionsLoading } =
    useClassDescriptionOptions({
      accountId: formMetaData?.accountId,
      industryCode: formMetaData?.industryCode,
      productType: formMetaData?.productType,
      effectiveDate:
        oneShotContext?.state?.effectiveDate ?? formMetaData?.effectiveDate,
      productVersion: formMetaData?.productVersion,
      state: formMetaData?.state,
    });

  const value = formContext.watch(props.name)?.props;

  // Get error and simplify verbose validation messages (only in one-shot UI)
  const fieldError = formContext.formState.errors[props.name];
  const errorMessage = React.useMemo(() => {
    if (!fieldError) {
      return props.error || '';
    }

    // In one-shot UI, apply error message simplification
    if (isInOneShotContext) {
      // Extract error message
      const getErrorMessage = (err: any): string => {
        if (!err || typeof err !== 'object') {
          return '';
        }
        if ('message' in err && typeof err.message === 'string') {
          return err.message;
        }
        // Recursively find first message
        for (const value of Object.values(err)) {
          const result = getErrorMessage(value);
          if (result) return result;
        }
        return '';
      };

      const rawError = getErrorMessage(fieldError);

      // Simplify verbose null/object type errors
      //if we don''t do this, we see errors like "860ddb61-856a-4c7c-877d-4c2d0863755e must be a `object` type, but the final value was: `null`. If "null" is intended as an empty value be sure to mark the schema as `.nullable()`"
      if (
        rawError.includes('must be a `object` type') &&
        rawError.includes('null')
      ) {
        return CLASS_DESC_ERROR;
      }

      return rawError;
    }

    // In non-one-shot UI, use the error prop as-is
    return props.error || '';
  }, [fieldError, props.error, isInOneShotContext]);

  // Clear field when selected value is no longer valid in options (only in one-shot UI)
  // Use isLoading to avoid race: only validate after options have finished loading.
  // When loaded: clear if options are empty (industry has no class desc) or value not in list.
  React.useEffect(() => {
    if (!isInOneShotContext || isOptionsLoading) {
      return;
    }

    const currentValueId = value?.id?.value;

    if (!currentValueId) {
      return;
    }

    const hasOptions =
      classDescriptionOptions && classDescriptionOptions.length > 0;
    const isValueValid =
      hasOptions &&
      classDescriptionOptions.some((opt) => opt.value === currentValueId);

    if (!isValueValid) {
      formContext.setValue(props.name, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      // Sync clear to applicationDetails so merged form values don't restore stale value.
      // pickAnsweredFields excludes null/empty, so debounced sync never updates this field.
      // Use { props: null } so normalizeValuePayload includes it in merged values (null is skipped).
      oneShotContext?.handlers?.setApplicationDetails({
        [props.name]: { props: null },
      } as any);
    }
  }, [
    classDescriptionOptions,
    value,
    formContext,
    props.name,
    isInOneShotContext,
    isOptionsLoading,
    oneShotContext?.handlers,
  ]);

  // Clear stale error when value becomes valid (e.g. after switching industry back).
  React.useEffect(() => {
    if (!isInOneShotContext || !value?.id?.value || !fieldError) return;
    const isVal =
      classDescriptionOptions?.some((o) => o.value === value?.id?.value) ??
      false;
    if (isVal) {
      formContext.trigger(props.name);
    }
  }, [
    isInOneShotContext,
    value,
    classDescriptionOptions,
    fieldError,
    props.name,
    formContext,
  ]);

  return (
    <>
      <StyledInputLabel required={Boolean(props.required)}>
        Please select the class description for this account{' '}
        {formMetaData ? (
          <>
            with an Industry code of <em>{formMetaData.industryCode}</em>
          </>
        ) : (
          ''
        )}
      </StyledInputLabel>

      <Autocomplete
        onChange={(_e, nextVal) =>
          nextVal &&
          formContext.setValue(
            `${props.name}.props`,
            transformObjToNestedValue(nextVal.meta),
            {
              shouldDirty: true,
              shouldValidate: true,
            }
          )
        }
        renderInput={(params) => <TextField name={props.name} {...params} />}
        options={classDescriptionOptions}
        value={value}
        isOptionEqualToValue={(opt, val) => {
          return opt.value === val?.id?.value;
        }}
        noOptionsText="No descriptions available"
        getOptionLabel={(opt) => opt.label || opt.description?.value}
        disabled={props.disabled}
      />

      <ErrorMessageText>{errorMessage}</ErrorMessageText>
    </>
  );
};

const transformObjToNestedValue = (obj: Record<string, any>) => {
  return Object.keys(obj).reduce((transformed, key) => {
    return {
      ...transformed,
      [key]: { value: obj[key] },
    };
  }, {});
};
